import React, { useEffect, useMemo, useState } from "react";
import styles from "./Subscription.module.css";
import Button from "../../component/button/Button";
import InputText from "../../component/inputText/InputText";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  createPromo,
  updatePromo,
  getSubscriptionPlans,
} from "../../api/HttpApis";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "var(--app-secondaryBg)",
  color: "var(--text-secondary)",
  padding: "2rem",
  borderRadius: "12px",
  width: "min(720px, 92vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  border: "1px solid var(--border-color)",
};

const InfoIcon = ({ text }) => (
  <span className={styles["info-icon"]} tabIndex={0} aria-label={text}>
    i
    <span className={styles["info-tooltip"]}>{text}</span>
  </span>
);

const FieldLabel = ({ children, info, required }) => (
  <div className={styles["label-row"]}>
    <span>
      {children}
      {required && " *"}
    </span>
    {info && <InfoIcon text={info} />}
  </div>
);

const ErrText = ({ msg }) =>
  msg ? <small className={styles["field-error"]}>{msg}</small> : null;

// Format a date for an <input type="date"> (YYYY-MM-DD)
const toDateInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};

const emptyForm = {
  name: "",
  description: "",
  intro_price: "",
  currency: "USD",
  duration_days: 90,
  valid_from: "",
  valid_until: "",
  status: 1,
  applies_to_plan_ids: [],
};

const PromoModal = ({ editData, onClose }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [plans, setPlans] = useState([]);

  // Load all active plans + their prices so admin can pick which prices the promo covers
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const response = await getSubscriptionPlans({ page: 1, limit: 100 });
        const list =
          response?.subscription_plans ||
          response?.data?.subscription_plans ||
          [];
        if (alive) setPlans(list);
      } catch (err) {
        console.error("Failed to load plans for promo modal", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        description: editData.description || "",
        intro_price: editData.intro_price ?? "",
        currency: editData.currency || "USD",
        duration_days: editData.duration_days || 90,
        valid_from: toDateInput(editData.valid_from),
        valid_until: toDateInput(editData.valid_until),
        status:
          editData.status === "Active" ? 1 : editData.status === "Paused" ? 0 : 1,
        applies_to_plan_ids: Array.isArray(editData.applies_to_plan_ids)
          ? editData.applies_to_plan_ids.map(String)
          : [],
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
  }, [editData]);

  const setField = (field, value) =>
    setFormData((f) => ({ ...f, [field]: value }));

  // Promos are tier-level: admin picks Basic / Premium / etc.
  // (Billing cycle is irrelevant — promo is one-time for duration_days of access.)
  // Free plans (no paid price) are excluded.
  const planChoices = useMemo(() => {
    const out = [];
    for (const plan of plans) {
      if (plan.status !== "Active") continue;
      const hasPaidPrice = (plan.prices || []).some((p) => Number(p.price) > 0);
      if (!hasPaidPrice) continue;
      out.push({ plan_id: String(plan.id), plan_name: plan.name });
    }
    return out;
  }, [plans]);

  const togglePlan = (planId) => {
    setFormData((f) => {
      const has = f.applies_to_plan_ids.includes(planId);
      return {
        ...f,
        applies_to_plan_ids: has
          ? f.applies_to_plan_ids.filter((id) => id !== planId)
          : [...f.applies_to_plan_ids, planId],
      };
    });
  };

  const isPlanChecked = (planId) =>
    formData.applies_to_plan_ids.includes(planId);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose(false);
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Name is required";
    if (
      formData.intro_price === "" ||
      isNaN(parseFloat(formData.intro_price)) ||
      parseFloat(formData.intro_price) < 0
    )
      e.intro_price = "Intro price must be 0 or greater";
    if (!formData.duration_days || parseInt(formData.duration_days) < 1)
      e.duration_days = "Duration must be at least 1 day";
    if (!formData.valid_from) e.valid_from = "Valid from is required";
    if (!formData.valid_until) e.valid_until = "Valid until is required";
    if (
      formData.valid_from &&
      formData.valid_until &&
      new Date(formData.valid_until) < new Date(formData.valid_from)
    )
      e.valid_until = "Valid until must be on or after Valid from";
    if (formData.applies_to_plan_ids.length === 0)
      e.applies_to_plan_ids = "Select at least one plan";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    const payload = {
      name: formData.name,
      description: formData.description || null,
      intro_price: parseFloat(formData.intro_price),
      currency: (formData.currency || "USD").toUpperCase(),
      duration_days: parseInt(formData.duration_days),
      valid_from: formData.valid_from,
      valid_until: formData.valid_until,
      status: parseInt(formData.status),
      applies_to_plan_ids: formData.applies_to_plan_ids.map((id) =>
        parseInt(id)
      ),
    };

    setLoading(true);
    try {
      let response;
      if (editData) {
        response = await updatePromo(editData.id, payload);
        showToast(response?.message || "Promo updated", "success");
      } else {
        response = await createPromo(payload);
        showToast(response?.message || "Promo created", "success");
      }
      onClose(true);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const flat = {};
        Object.entries(apiErrors).forEach(([k, v]) => {
          flat[k] = Array.isArray(v) ? v[0] : v;
        });
        setErrors((prev) => ({ ...prev, ...flat }));
      }
      showToast(
        err?.response?.data?.message || "Failed to save promo",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={handleOverlayClick}>
      <div style={modalStyle}>
        <h2 style={{ marginTop: 0, color: "var(--text-dark)" }}>
          {editData ? "Edit Promo" : "Add New Promo"}
        </h2>

        <form onSubmit={handleSubmit} className={styles["api-form"]}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: "1rem",
            }}
          >
            <div className={styles["form-group"]}>
              <FieldLabel
                required
                info="Internal name for this promo (e.g. 'Launch Offer 2026')."
              >
                Name
              </FieldLabel>
              <InputText
                value={formData.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Launch Offer"
                className={styles["input-dark"]}
              />
              <ErrText msg={errors.name} />
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel info="Paused promos won't apply to new signups. Existing users on the promo keep their discount window.">
                Status
              </FieldLabel>
              <select
                value={formData.status}
                onChange={(e) => setField("status", e.target.value)}
                className={styles["plan-field-select"]}
              >
                <option value={1}>Active</option>
                <option value={0}>Paused</option>
              </select>
            </div>
          </div>

          <div className={styles["form-group"]}>
            <FieldLabel info="Optional short note for your team. Not shown to users.">
              Description
            </FieldLabel>
            <textarea
              value={formData.description}
              onChange={(e) => setField("description", e.target.value)}
              className={styles["plan-field-textarea"]}
              rows={2}
              placeholder="$2.99 for the first 90 days"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className={styles["form-group"]}>
              <FieldLabel
                required
                info="Amount charged during the intro window (e.g. 2.99)."
              >
                Intro Price
              </FieldLabel>
              <InputText
                type="number"
                value={formData.intro_price}
                onChange={(e) => setField("intro_price", e.target.value)}
                placeholder="2.99"
                className={styles["input-dark"]}
              />
              <ErrText msg={errors.intro_price} />
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel info="3-letter ISO code (USD, INR, EUR, …)">
                Currency
              </FieldLabel>
              <InputText
                value={formData.currency}
                onChange={(e) =>
                  setField("currency", e.target.value.toUpperCase())
                }
                placeholder="USD"
                className={styles["input-dark"]}
              />
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel
                required
                info="How many days each user gets the intro price. After this, regular billing kicks in."
              >
                Duration (days)
              </FieldLabel>
              <InputText
                type="number"
                value={formData.duration_days}
                onChange={(e) => setField("duration_days", e.target.value)}
                placeholder="90"
                className={styles["input-dark"]}
              />
              <ErrText msg={errors.duration_days} />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className={styles["form-group"]}>
              <FieldLabel
                required
                info="First date the promo is available to new signups."
              >
                Valid From
              </FieldLabel>
              <input
                type="date"
                value={formData.valid_from}
                onChange={(e) => setField("valid_from", e.target.value)}
                className={styles["plan-field-select"]}
              />
              <ErrText msg={errors.valid_from} />
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel
                required
                info="Last date a new user can claim the promo. Users already on it keep their discount window."
              >
                Valid Until
              </FieldLabel>
              <input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setField("valid_until", e.target.value)}
                className={styles["plan-field-select"]}
              />
              <ErrText msg={errors.valid_until} />
            </div>
          </div>

          <div className={styles["form-group"]}>
            <FieldLabel
              required
              info="Which plans (tiers) does this promo apply to? Selecting a plan covers all its billing cycles automatically. Free plans are excluded."
            >
              Applies to plans
            </FieldLabel>
            {planChoices.length === 0 ? (
              <div className={styles["prices-empty"]}>
                No paid plans found. Create a paid plan first.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  padding: "10px",
                }}
              >
                {planChoices.map((c) => (
                  <label
                    key={c.plan_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isPlanChecked(c.plan_id)}
                      onChange={() => togglePlan(c.plan_id)}
                    />
                    {c.plan_name}
                  </label>
                ))}
              </div>
            )}
            <ErrText msg={errors.applies_to_plan_ids} />
          </div>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <Button
              variant="outline"
              type="button"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              {editData ? "Update Promo" : "Create Promo"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromoModal;
